import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Button,
    Box,
    Alert,
    Stack,
    DialogActions
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../../../../../app/hooks';
import { addProcedureCode, updateProcedureCode, removeProcedureCode } from '../../../../../../services/ContractService/contractEstimateSlice';
import { useGetProceduresQuery } from '../../../../../../services/ConfigurationService';
import { ProcedureCodeEstimate } from '../../../../../../services/ContractService/types/contractEstimateTypes';
import { useEstProcedureForm, useProcedureSearch } from './hooks';
import {
    SelectedProcedureCard,
    ProcedureSelectionGrid,
    ProcedureDetailsForm,
    DialogActionButtons
} from './components';

interface AddEditEstProcedureDialogProps {
    open: boolean;
    editingProcedure: ProcedureCodeEstimate | null;
    onClose: () => void;
}

const AddEditEstProcedureDialog: React.FC<AddEditEstProcedureDialogProps> = ({
    open,
    editingProcedure,
    onClose,
}) => {
    const [submitError, setSubmitError] = useState<string>('');

    const dispatch = useAppDispatch();
    const estimate = useAppSelector(state => state.contractEstimate.currentEstimate);

    // Get procedure codes for the current center
    const {
        data: availableProcedures = [],
        isLoading: proceduresLoading,
        error: proceduresError
    } = useGetProceduresQuery(
        { centerId: estimate?.centerId || '' },
        { skip: !estimate?.centerId || !open }
    );

    const {
        formData,
        formErrors,
        handleFormChange,
        handleProcedureSelect,
        clearSelectedProcedure,
        validateForm
    } = useEstProcedureForm({
        editingProcedure,
        availableProcedures,
        open
    });

    const {
        searchTerm,
        setSearchTerm,
        filteredProcedures,
        resetSearch
    } = useProcedureSearch({
        availableProcedures
    });

    // Reset search when dialog opens
    useEffect(() => {
        if (open) {
            resetSearch();
            setSubmitError('');
        }
    }, [open, resetSearch]);

    // Handle grid row selection
    const handleRowSelection = (args: any) => {
        if (args.data) {
            const selectedProcedure = filteredProcedures.find(proc => proc.id === args.data.id);
            if (selectedProcedure) {
                handleProcedureSelect(selectedProcedure);
            }
        }
    };

    const handleSubmit = async () => {
        if (!validateForm() || !formData.selectedProcedure) {
            return;
        }

        try {
            if (editingProcedure) {
                // Update existing procedure
                dispatch(updateProcedureCode({
                    id: editingProcedure.id,
                    updates: {
                        priority: formData.priority,
                        code: formData.selectedProcedure.code,
                        description: formData.selectedProcedure.description,
                        units: formData.units,
                        implantCost: formData.implantCost,
                        procedureType: formData.procedureType,
                        billedCharges: formData.billedCharges,
                        revenueCode: formData.revenueCode,
                        modifiers: formData.modifiers,
                        apcCode: formData.apcCode || undefined,
                    }
                }));
            } else {
                // Add new procedure
                dispatch(addProcedureCode({
                    priority: formData.priority,
                    code: formData.selectedProcedure.code,
                    description: formData.selectedProcedure.description,
                    units: formData.units,
                    implantCost: formData.implantCost,
                    procedureType: formData.procedureType,
                    billedCharges: formData.billedCharges,
                    revenueCode: formData.revenueCode,
                    modifiers: formData.modifiers,
                    apcCode: formData.apcCode || undefined,
                }));
            }
            onClose();
        } catch (error: any) {
            console.error('Failed to save procedure code:', error);
            setSubmitError('Failed to save procedure code. Please try again.');
        }
    };

    const handleDelete = () => {
        if (editingProcedure) {
            dispatch(removeProcedureCode(editingProcedure.id));
            onClose();
        }
    };

    const isEditing = !!editingProcedure;
    const dialogTitle = isEditing ? 'Edit Procedure Code' : 'Add New Procedure Code';

    // Show warning if no center selected
    if (!estimate?.centerId) {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle>Add Procedure Code</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        Please select a center for the estimate before adding procedure codes.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={onClose}
                        sx={{
                            color: '#64748b',
                            '&:hover': {
                                backgroundColor: '#f1f5f9',
                                color: '#475569'
                            }
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>{dialogTitle}</DialogTitle>
            <DialogContent>
                <Box sx={{ pt: 1 }}>
                    {submitError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {submitError}
                        </Alert>
                    )}

                    {proceduresError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            Failed to load procedure codes. Please try again.
                        </Alert>
                    )}

                    <Stack spacing={3}>
                        {/* Selected Procedure Display */}
                        {formData.selectedProcedure && (
                            <SelectedProcedureCard
                                procedure={formData.selectedProcedure}
                                onClear={clearSelectedProcedure}
                            />
                        )}

                        {/* Procedure Selection */}
                        {!formData.selectedProcedure && (
                            <ProcedureSelectionGrid
                                searchTerm={searchTerm}
                                onSearchTermChange={setSearchTerm}
                                filteredProcedures={filteredProcedures}
                                isLoading={proceduresLoading}
                                error={formErrors.selectedProcedure}
                                onRowSelection={handleRowSelection}
                            />
                        )}

                        {/* Procedure Details Form (shown when procedure is selected) */}
                        {formData.selectedProcedure && (
                            <ProcedureDetailsForm
                                priority={formData.priority}
                                units={formData.units}
                                implantCost={formData.implantCost}
                                procedureType={formData.procedureType}
                                billedCharges={formData.billedCharges}
                                revenueCode={formData.revenueCode}
                                modifiers={formData.modifiers}
                                apcCode={formData.apcCode}
                                errors={formErrors}
                                onPriorityChange={(value) => handleFormChange('priority', value)}
                                onUnitsChange={(value) => handleFormChange('units', value)}
                                onImplantCostChange={(value) => handleFormChange('implantCost', value)}
                                onProcedureTypeChange={(value) => handleFormChange('procedureType', value)}
                                onBilledChargesChange={(value) => handleFormChange('billedCharges', value)}
                                onRevenueCodeChange={(value) => handleFormChange('revenueCode', value)}
                                onModifiersChange={(value) => handleFormChange('modifiers', value)}
                                onApcCodeChange={(value) => handleFormChange('apcCode', value)}
                            />
                        )}
                    </Stack>
                </Box>
            </DialogContent>

            <DialogActionButtons
                isEditing={isEditing}
                isSubmitDisabled={!formData.selectedProcedure}
                onDelete={isEditing ? handleDelete : undefined}
                onCancel={onClose}
                onSubmit={handleSubmit}
            />
        </Dialog>
    );
};

export default AddEditEstProcedureDialog;
